// Capa 1 del plan "una sola conversacion por caso" (caso #20261022620039): que la respuesta salga desde el caso.
// Funciones puras, con pruebas:
// - casoDelHilo: si un correo es de un caso abierto (unido, con [Ticket#N] o con el mismo asunto y su cliente).
// - esReenvioDeCompanero: el correo es un reenvio que me hizo alguien del equipo (algo que ya puede ser un caso).
// - conCopiaAlGrupo: si igual se responde desde el buzon personal, copia oculta al buzon del grupo (D60), para que la
//   respuesta llegue al caso.
// Misma normalizacion de asunto que el servicio de correo (correo/api/relacionados.mjs).

const lc = (s?: string | null) => String(s ?? '').trim().toLowerCase();

// Asunto comparable: sin RE:/FW:/RV:/Fwd: (repetidos), sin [Ticket#N] / [#N], sin tildes, espacios de mas ni mayusculas.
export function normalizarAsunto(asunto: string) {
  let s = String(asunto ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
  s = s.replace(/\[(ticket)?#\d+\]/g, ' ');
  s = s.replace(/^(\s*(re|fwd?|rv|res|enc|tr|aw|wg)\s*(\[\d+\])?\s*:)+/, '');
  return s.replace(/\s+/g, ' ').trim();
}

const GENERICOS = new Set(['consulta', 'consultas', 'cotizacion', 'cotizaciones', 'informacion', 'solicitud', 'soporte', 'soporte tecnico', 'ayuda', 'urgente', 'hola', 'propuesta', 'factura', 'facturas', 'pedido', 'reunion', 'sin asunto', '(sin asunto)', 'seguimiento', 'pregunta', 'saludos', 'importante', 'documentos', 'pago', 'pagos', 'contrato', 'licencia', 'licencias', 'renovacion', 'problema', 'error', 'prueba', 'test', 'requerimiento', 'incidencia', 'reclamo']);

// Un asunto corto o generico ("Consulta", "Cotizacion 2") nunca dice de que caso es: hay demasiados iguales.
export function asuntoGenerico(clave: string) {
  const base = clave.replace(/[\s\d.,:;#\-_()]+$/g, '').trim();
  return clave.length < 12 || GENERICOS.has(clave) || GENERICOS.has(base);
}

// Numero de ticket del asunto ([Ticket#N] o [#N]), o null.
export function numeroTicket(asunto: string): string | null {
  const m = /\[Ticket#(\d{4,20})\]/i.exec(asunto ?? '') ?? /\[#(\d{10,20})\]/.exec(asunto ?? '');
  return m ? m[1] : null;
}

export type CasoCandidato = { id: string; numero: number | null; asunto: string; grupo: string; correoCliente: string; hiloId: string | null; hilosUnidos: string[] };
export type CasoDelHilo<C extends CasoCandidato = CasoCandidato> = { caso: C; motivo: 'unido' | 'ticket' | 'asunto' };

// De que caso abierto es un correo. `casos`: los abiertos que la persona ve. `participantes`: todas las direcciones del
// hilo. Por asunto solo si coincide un unico caso y su cliente esta en el hilo (nunca por un asunto generico).
export function casoDelHilo<C extends CasoCandidato>(o: { hiloId?: string | null; asunto: string; participantes: string[]; casos: C[] }): CasoDelHilo<C> | null {
  if (o.hiloId) {
    const c = o.casos.find((x) => x.hiloId === o.hiloId || x.hilosUnidos.includes(o.hiloId!));
    if (c) return { caso: c, motivo: 'unido' };
  }
  const t = numeroTicket(o.asunto);
  if (t) {
    const c = o.casos.find((x) => String(x.numero ?? '') === t);
    // Un [Ticket#N] de un caso que no esta abierto (o que no veo) no se adivina por asunto: seria de otro caso.
    return c ? { caso: c, motivo: 'ticket' } : null;
  }
  const clave = normalizarAsunto(o.asunto);
  if (!clave || asuntoGenerico(clave)) return null;
  const quienes = new Set(o.participantes.map(lc));
  const iguales = o.casos.filter((x) => normalizarAsunto(x.asunto) === clave && !!x.correoCliente && quienes.has(lc(x.correoCliente)));
  return iguales.length === 1 ? { caso: iguales[0], motivo: 'asunto' } : null;
}

// Un reenvio (FW/RV/Fwd) que me hizo alguien del equipo: lo importante ya suele ser un caso.
export function esReenvioDeCompanero(m: { asunto: string; de: string } | null | undefined, mios: string[], equipo: (correo: string) => boolean) {
  if (!m) return false;
  const de = lc(m.de);
  return /^\s*(fw|fwd|rv|reenv[ií]o)\s*:/i.test(m.asunto ?? '') && !!de && equipo(de) && !mios.map(lc).includes(de);
}

// Copia oculta al buzon del grupo del caso cuando se responde desde un buzon personal: la respuesta llega al caso por el
// camino de siempre. No se repite si ya va en Para, CC o CCO.
export function conCopiaAlGrupo(e: { para: string[]; cc: string[]; cco: string[] }, grupo: string | null | undefined) {
  const g = lc(grupo);
  if (!g || !g.includes('@') || [...e.para, ...e.cc, ...e.cco].some((x) => lc(x) === g)) return e.cco;
  return [...e.cco, g];
}

// Asunto para responder desde el caso: con [Ticket#N] (una sola vez) para que la respuesta del cliente vuelva al caso.
export function asuntoConTicket(asunto: string, numero: number | null) {
  if (!numero || numeroTicket(asunto) === String(numero)) return asunto;
  return `${asunto.replace(/\s*\[(Ticket)?#\d+\]\s*/gi, ' ').trim()} [Ticket#${numero}]`;
}

// Direcciones de la empresa: las dadas (miembros y buzones de grupo) y cualquiera de sus dominios propios (nunca un
// dominio publico como gmail.com).
const PUBLICOS = new Set(['gmail.com', 'googlemail.com', 'hotmail.com', 'outlook.com', 'live.com', 'msn.com', 'yahoo.com', 'icloud.com', 'aol.com', 'proton.me', 'protonmail.com']);
export function equipoDe(correos: string[]): (correo: string) => boolean {
  const propios = new Set(correos.map(lc).filter((c) => c.includes('@')));
  const dominios = new Set([...propios].map((c) => c.slice(c.lastIndexOf('@') + 1)).filter((d) => d && !PUBLICOS.has(d)));
  return (correo) => {
    const c = lc(correo);
    return c.includes('@') && (propios.has(c) || dominios.has(c.slice(c.lastIndexOf('@') + 1)));
  };
}
